import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Paper, Typography } from "@mui/material";
import { CheckCircleOutline, InfoOutlined, Phone } from "@mui/icons-material";
import * as Sentry from "@sentry/react";
import dayjs from "dayjs";
import {
  Callout,
  ConsentRow,
  InfoBanner,
  OtpInput,
  PageHeader,
  StepActions,
  TypographyLegal,
  TypographyOtpHelp,
} from "@components/ui_v2";
import { ConfirmationDialog } from "@components/ConexionSat/ConfirmationDialog";
import { RootState, useAppDispatch } from "@store/store";
import { setBuroNip, setBuroNipLastCodeRequest, setCheckSatBureau } from "@store/slices/registerSlice";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { useRequestPhoneCodeMutation, useValidatePhoneCodeMutation } from "@api/onboarding";
import { useLazyGetPersonByIdQuery, useLazyGetPersonsQuery } from "@api/personasApi";
import { CiecValidatorRequest, useBuroValidatorMutation, useCiecValidatorMutation } from "@api/sat_apis_new/satApsi";
import { usePostValidateNextStageMutation } from "@api/applications";
import { ApiError } from "@interfaces/response";
import { NipBuroVariant } from "@interfaces/nipBuro";
import { PATHS_TOFU_V2 } from "@helpers/constantes";
import { formatPhoneNumber, getRemainingTime } from "@helpers/funciones";
import { NIP_BURO_CONFIG, buildBuroPayloadPfae, getNipBuroPhone } from "@helpers/nipBuroHelpers";
import useStatusConfig from "@hooks/useStatusConfig";

const TIME_LIMIT = 120;

interface Props {
  variant: NipBuroVariant;
}

/**
 * "Autoriza la consulta a Buró de Crédito": NIP por SMS/WhatsApp, autorización (reingresando el código,
 * como el modal legacy) y consulta a Buró. Por ahora solo PFAE (legacy `tofu/pm/ConsultaBuroNipPfae` +
 * `ModalBuroNipPfae`); las demás personas se agregan como variantes al migrarlas (`NipBuroVariant`).
 */
export const NipBuro = ({ variant }: Props) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { screenStatus, checkSatBureau } = NIP_BURO_CONFIG[variant];
  const { nextStatus, previousStatus } = useStatusConfig(screenStatus);

  const company = useSelector((state: RootState) => state.register.company);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const application = useSelector((state: RootState) => state.register.application);
  const buroNip = useSelector((state: RootState) => state.register.buroNip);
  const brand = useSelector((state: RootState) => state.brand);

  const [validatePhoneCode] = useValidatePhoneCodeMutation();
  const [requestPhoneCode, { isLoading: isRequestingCode }] = useRequestPhoneCodeMutation();
  const [getPersons] = useLazyGetPersonsQuery();
  const [getPersonById] = useLazyGetPersonByIdQuery();
  const [buroValidator] = useBuroValidatorMutation();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [ciecValidator] = useCiecValidatorMutation();

  const resendEffectRanRef = useRef(false);

  const [otp, setOtp] = useState("");
  const [modalOtp, setModalOtp] = useState("");
  const [secondsRemaining, setSecondsRemaining] = useState(TIME_LIMIT);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [rateLimited, setRateLimited] = useState(false);
  const [justResent, setJustResent] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const phone = getNipBuroPhone(company);
  const otpConfirmCorrecto = modalOtp.length === 4 && modalOtp === buroNip.code;

  useEffect(() => {
    if (secondsRemaining <= 0) return;
    const t = setTimeout(() => setSecondsRemaining((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsRemaining]);

  // Si el último envío fue hace menos de 2 min, solo se retoma la cuenta (como `ConsultaBuroNipPF`).
  const handleResendCode = async () => {
    try {
      setIsLoading(true);
      setRateLimited(false);

      if (buroNip.isValid || isRequestingCode || !phone.countryCode || !phone.number) return;

      const secondsToWait = getRemainingTime(buroNip.last_code_request || "", TIME_LIMIT);
      if (secondsToWait > 0) {
        setSecondsRemaining(secondsToWait);
        return;
      }

      dispatch(setBuroNipLastCodeRequest({ last_code_request: dayjs().toISOString() }));
      try {
        await requestPhoneCode({ country_code: phone.countryCode, phone_number: phone.number }).unwrap();
      } catch (requestError) {
        if ((requestError as ApiError)?.status !== 429) {
          dispatch(setBuroNipLastCodeRequest({ last_code_request: null }));
        }
        throw requestError;
      }

      setSecondsRemaining(TIME_LIMIT);
      setJustResent(true);
      setTimeout(() => setJustResent(false), 4000);
    } catch (error) {
      const apiError = error as ApiError;
      if (apiError?.status === 429) {
        setRateLimited(true);
        return;
      }
      Sentry.captureException(error, { extra: { component: `NipBuro-${variant}-resend` } });
      dispatch(setModalErrorVisible({ open: true, error: apiError }));
    } finally {
      setIsLoading(false);
    }
  };

  const validateCode = async () => {
    try {
      if (buroNip.isValid) {
        setErrorMessage("");
        return;
      }
      if (otp.length < 4) return;
      if (!phone.countryCode || !phone.number) {
        setErrorMessage("No se encontró el número de teléfono");
        return;
      }

      setIsLoading(true);
      setErrorMessage("");

      await validatePhoneCode({ country_code: phone.countryCode, phone_number: phone.number, code: otp }).unwrap();

      dispatch(setBuroNip({ code: otp, isValid: true, termsAccepted: false }));
    } catch (error) {
      const apiError = error as ApiError;
      if (apiError?.status === 404 || apiError?.status === 400) {
        setErrorMessage("Código incorrecto, intenta de nuevo");
        setOtp("");
        return;
      }
      Sentry.captureException(apiError, { extra: { component: `NipBuro-${variant}-validate`, values: { otp } } });
      dispatch(setModalErrorVisible({ open: true, error: apiError }));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    validateCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  // Al entrar se manda el NIP (si no está validado); si ya lo estaba, se muestra el código.
  useEffect(() => {
    if (!buroNip.isValid && !resendEffectRanRef.current) {
      resendEffectRanRef.current = true;
      handleResendCode();
    }
    if (buroNip.isValid && buroNip.code) {
      setOtp(buroNip.code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buroNip.isValid]);

  // Como el prototipo: con el código validado, el check abre la confirmación; una vez autorizado ya no se quita.
  const handleConsentClick = () => {
    if (!buroNip.isValid || buroNip.termsAccepted) return;
    setModalOtp("");
    setShowConfirmModal(true);
  };

  const handleAcceptConsent = () => {
    if (!otpConfirmCorrecto) return;
    dispatch(setBuroNip({ code: buroNip.code, isValid: true, termsAccepted: true }));
    setShowConfirmModal(false);
    setModalOtp("");
  };

  const validarCiec = async () => {
    const payload: CiecValidatorRequest = {
      rfc: company.rfc,
      ciec: "",
      application_code: application.company_application_code,
      client_details_id: String(application.client_details_id),
      loan_application_conditions_id: String(application.application_condition_id),
      loan_application_id: String(application.id),
      requested_amount: Number(application.loan_amount),
      requested_term: Number(application.loan_term),
      company_id: brand?.id ?? "",
    };
    await ciecValidator(payload).unwrap();
  };

  /**
   * Como `ModalBuroNipPfae`: la persona es la primera de la solicitud y, si su autorización ya se mandó y
   * fue correcta, no se vuelve a consultar.
   */
  const consultarBuro = async () => {
    const persons = await getPersons({ loanApplicationId: String(application.id) }).unwrap();
    const person = persons.length > 0 ? await getPersonById(persons[0].id).unwrap() : null;
    if (person?.is_credit_bureau_authorization_pf_sended && person?.is_credit_bureau_authorization_pf_correct) {
      return;
    }
    await buroValidator(buildBuroPayloadPfae(company, application, person ? String(person.id) : "")).unwrap();
  };

  const continueToNextStatus = async () => {
    if (!nextStatus) throw new Error("No se encontró el siguiente status");

    await triggerPostNextStage({
      applicationId: application.id,
      statusCode: nextStatus.code,
      username: `TOFU - ${accountUser.email}`,
    }).unwrap();

    dispatch(setCheckSatBureau({ check_sat_bureau: checkSatBureau }));
    navigate(nextStatus.url);
  };

  const handleContinue = async () => {
    if (!buroNip.isValid || !buroNip.termsAccepted) return;

    setIsLoading(true);
    try {
      await validarCiec();
      await consultarBuro();
      await continueToNextStatus();
    } catch (error) {
      const apiError = error as ApiError;
      // 409: la consulta ya se había hecho; se sigue igual que en el legacy.
      if (apiError.status === 409) {
        try {
          await continueToNextStatus();
        } catch (continueError) {
          Sentry.captureException(continueError, { extra: { component: `NipBuro-${variant}-continue` } });
          dispatch(setModalErrorVisible({ open: true, error: continueError as ApiError }));
        }
        return;
      }
      Sentry.captureException(error, { extra: { component: `NipBuro-${variant}-consulta` } });
      dispatch(setModalErrorVisible({ open: true, error: apiError }));
    } finally {
      setIsLoading(false);
    }
  };

  const autorizado = buroNip.termsAccepted;

  return (
    <Box>
      {/* `S_NIPBuro` del prototipo, versión PFAE. */}
      <PageHeader
        eyebrow="Autorización · Buró de crédito"
        title="Autoriza la consulta a tu buró"
        subtitle="Con un solo NIP consultamos tu historial como negocio y como persona física. Enviamos un código de 4 dígitos por SMS y WhatsApp."
      />

      <Box sx={{ mb: 2 }}>
        <InfoBanner
          icon={<Phone fontSize="small" />}
          label="Teléfono celular"
          value={phone.number ? `(${phone.countryCode}) ${formatPhoneNumber(phone.number)}` : "—"}
          editLabel="Cambiar"
          // Como el legacy, el teléfono del titular se corrige en el registro.
          onEdit={buroNip.isValid ? undefined : () => navigate(PATHS_TOFU_V2.registroDatosPersonales.path)}
        />
      </Box>

      {autorizado ? (
        <Paper variant="lendia_paper" sx={{ textAlign: "center", p: "32px 24px" }}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              bgcolor: "#ECFDF3",
              color: "success.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <CheckCircleOutline sx={{ fontSize: 26 }} />
          </Box>
          <Typography
            sx={{
              fontFamily: "Outfit",
              fontSize: 18,
              fontWeight: 600,
              letterSpacing: "-0.01em",
              color: "primary.dark",
            }}
          >
            Autorización registrada
          </Typography>
          <Typography sx={{ fontSize: 13, color: "tokens.textTertiary", lineHeight: 1.55, mt: 1, mb: 2.5 }}>
            Quedan autorizadas las dos consultas: Buró de Crédito Empresas y Buró de Crédito Personas.
          </Typography>
          <ConsentRow checked onChange={() => undefined} disabled label={<TypographyLegal type="autorizacionBuro" />} />
        </Paper>
      ) : (
        <>
          {/* `.ld-verify-card`: la misma tarjeta del código que la validación de teléfono. */}
          <Paper
            variant="lendia_paper"
            sx={{
              textAlign: "center",
              p: "36px 28px 28px",
              boxShadow: "0 1px 2px rgba(14, 31, 58, 0.06), 0 1px 3px rgba(14, 31, 58, 0.04)",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <OtpInput length={4} value={otp} onChange={setOtp} size="lg" disabled={isLoading || buroNip.isValid} />
            </Box>

            {errorMessage && (
              <Box sx={{ mt: 2 }}>
                <Callout tone="warn">{errorMessage}</Callout>
              </Box>
            )}

            {rateLimited && (
              <Box sx={{ mt: 2 }}>
                <Callout tone="warn">
                  Excedió el número de intentos permitidos para ese número, intenta más tarde o contacta a un asesor.
                </Callout>
              </Box>
            )}

            {buroNip.isValid ? (
              <Typography sx={{ mt: 2, fontSize: 13, fontWeight: 500, color: "success.main" }}>
                Código verificado
              </Typography>
            ) : (
              <TypographyOtpHelp
                mt={2}
                justResent={justResent}
                secondsRemaining={secondsRemaining}
                onResend={handleResendCode}
                resendDisabled={isLoading}
                testIdPrefix="nip-buro"
              />
            )}
          </Paper>

          <Paper variant="lendia_paper" sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1.25 }}>
            <ConsentRow
              bare
              checked={false}
              onChange={handleConsentClick}
              disabled={!buroNip.isValid || isLoading}
              label={<TypographyLegal type="autorizacionBuro" />}
            />
            <Callout tone="info" icon={<InfoOutlined sx={{ fontSize: 16 }} />}>
              <Box component="span" sx={{ fontSize: 12.5, lineHeight: 1.55 }}>
                <TypographyLegal type="avisoEvaluacion" />
              </Box>
            </Callout>
          </Paper>
        </>
      )}

      <StepActions
        onBack={() => previousStatus && navigate(previousStatus.url)}
        onNext={handleContinue}
        nextLabel={autorizado ? "Continuar al análisis" : "Continuar"}
        nextDisabled={!autorizado || isLoading}
        backTestId="nip-buro-back-button"
        nextTestId="nip-buro-continue-button"
      />

      <ConfirmationDialog
        open={showConfirmModal}
        title="Consulta a Buró de Crédito"
        onClose={() => setShowConfirmModal(false)}
        onAccept={handleAcceptConsent}
        closeLabel="Cancelar"
        acceptLabel="Acepto y autorizo"
        acceptDisabled={!otpConfirmCorrecto}
      >
        <Typography component="div" sx={{ fontSize: 13.5, color: "tokens.textSecondary", lineHeight: 1.65, mb: 2 }}>
          <TypographyLegal type="modalBuro" />
        </Typography>
        <Box sx={{ borderTop: "1px solid", borderColor: "divider", pt: 2 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 500, color: "primary.dark", mb: 1.25 }}>
            Confirma tu identidad reingresando el código NIP
          </Typography>
          <OtpInput length={4} value={modalOtp} onChange={setModalOtp} />
          {modalOtp.length === 4 && !otpConfirmCorrecto && (
            <Typography sx={{ fontSize: 12.5, color: "error.main", mt: 1 }}>
              Código incorrecto. Inténtalo de nuevo.
            </Typography>
          )}
        </Box>
      </ConfirmationDialog>
    </Box>
  );
};
